import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { InfoPageLayout } from "@/components/pages/InfoPageLayout";
import { Mail, Bug, Sparkles, Handshake, BookOpen, Clock } from "lucide-react";
import { ContactForm } from "@/components/contact/ContactForm";
import {
  webPageSchema, breadcrumbSchema, organizationSchema, ldScripts,
} from "@/lib/seo/schema";
import { z } from "zod";

const TITLE = "Kontak - Kalender Indonesia";
const DESC = "Hubungi tim Kalender Indonesia untuk pertanyaan, saran, koreksi data libur, atau kerja sama.";

export const Route = createFileRoute("/kontak")({
  validateSearch: (s) =>
    z.object({ topic: z.string().optional() }).parse(s),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: "Kontak Kalender Indonesia" },
      { property: "og:description", content: "Hubungi kami untuk pertanyaan, saran, atau koreksi data." },
    ],
    links: [{ rel: "canonical", href: "https://hari.co.id/kontak/" }],
    scripts: ldScripts([
      webPageSchema({ url: "/kontak", name: TITLE, description: DESC, type: "ContactPage", locale: "id" }),
      breadcrumbSchema([
        { name: "Beranda", url: "/" },
        { name: "Kontak", url: "/kontak" },
      ]),
      organizationSchema("id"),
    ]),
  }),
  component: KontakPage,
});

const TOPICS = [
  { icon: BookOpen, label: "Koreksi data libur" },
  { icon: Sparkles, label: "Permintaan fitur" },
  { icon: Bug, label: "Laporan bug" },
  { icon: Handshake, label: "Kerja sama / iklan" },
];

function KontakPage() {
  const { topic } = Route.useSearch();
  const isKoreksi = topic === "koreksi";
  const defaultTopic = isKoreksi ? "Koreksi data libur" : undefined;
  const prefillMessage = isKoreksi
    ? "Halaman: \nBagian yang salah: \nUsulan koreksi: \n"
    : "";
  return (
    <SiteShell>
      <InfoPageLayout
        variant="contact"
        title="Kontak"
        subtitle="Saran, koreksi data, atau kerja sama. Kami senang mendengar dari Anda."
        badge="@"
        locale="id"
        breadcrumbs={[{ label: "Beranda", href: "/" }, { label: "Kontak" }]}
        aside={
          <div className="rounded-xl border border-border bg-card p-4 text-sm">
            <h3 className="mb-2 font-semibold">Lainnya</h3>
            <ul className="space-y-1.5 text-muted-foreground">
              <li><a className="hover:text-primary" href="/tentang">Tentang</a></li>
              <li><a className="hover:text-primary" href="/kebijakan-privasi">Kebijakan Privasi</a></li>
              <li><a className="hover:text-primary" href="/syarat-ketentuan">Syarat &amp; Ketentuan</a></li>
            </ul>
          </div>
        }
      >
        {isKoreksi && (
          <div className="not-prose mb-4 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
            <strong>Form koreksi redaksi.</strong>{" "}
            Sertakan tautan halaman dan redaksi yang perlu diperbaiki agar tim
            kami bisa menindaklanjuti dengan cepat.
          </div>
        )}
        <p>
          Terima kasih telah menggunakan <strong>Kalender Indonesia</strong>.
          Jika Anda menemukan kesalahan data, ingin memberi saran fitur, atau
          mengusulkan kerja sama, silakan hubungi kami melalui kanal berikut.
        </p>

        <div className="not-prose mt-6 grid gap-3 sm:grid-cols-2">
          <a
            href="mailto:support@hari.co.id"
            className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Mail className="h-5 w-5" />
            </span>
            <span>
              <span className="block text-xs uppercase tracking-wide text-muted-foreground">Email</span>
              <span className="block font-semibold">support@hari.co.id</span>
            </span>
          </a>
          <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Clock className="h-5 w-5" />
            </span>
            <span>
              <span className="block text-xs uppercase tracking-wide text-muted-foreground">Waktu respon</span>
              <span className="block font-semibold">2–5 hari kerja</span>
            </span>
          </div>
        </div>

        <h2 id="topik">Topik yang bisa dikirim</h2>
        <div className="not-prose mt-3 flex flex-wrap gap-2">
          {TOPICS.map(({ icon: Icon, label }) => (
            <span key={label} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-sm">
              <Icon className="h-3.5 w-3.5 text-primary" /> {label}
            </span>
          ))}
        </div>

        <h2 id="form">Kirim pesan</h2>
        <p>
          Isi formulir di bawah. Pesan Anda akan dikirim langsung ke tim
          dukungan hari.co.id melalui aplikasi email Anda.
        </p>
        <ContactForm locale="id" defaultTopic={defaultTopic} prefillMessage={prefillMessage} />
      </InfoPageLayout>
    </SiteShell>
  );
}